import Colors from '@constants/Colors';
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

interface PhotoGalleryModalProps {
    open: boolean;
    onClose: () => void;
    images: string[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: '#242629',
        borderRadius: '12px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: '100%',
        height: '100%',
        margin: '20px',
        display: 'flex',
        flexDirection: 'column',
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
}));

const ModalHeader = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 32px',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: Colors.NEUTRAL_WHITE,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    width: '40px',
    height: '40px',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
}));

const MainImageContainer = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '0 80px 24px 80px',
    minHeight: 0, // Important for flexbox child
}));

const MainModalImage = styled('img')(() => ({
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    borderRadius: '12px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.3)',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: Colors.NEUTRAL_WHITE,
    color: Colors.NEUTRAL_BLACK,
    width: '48px',
    height: '48px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    '&:hover': {
        backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
    },
    '&:disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        color: 'rgba(0, 0, 0, 0.3)',
    },
}));

const PrevButton = styled(NavigationButton)(() => ({
    left: '24px',
}));

const NextButton = styled(NavigationButton)(() => ({
    right: '24px',
}));

const ThumbnailStrip = styled(Box)(() => ({
    display: 'flex',
    overflowX: 'auto',
    gap: '12px',
    padding: '16px 32px 32px 32px',
    '&::-webkit-scrollbar': {
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '3px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
}));

const ThumbnailWrapper = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
    minWidth: '80px',
    height: '60px',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: isActive ? `2px solid ${Colors.PRIMARY_PURPLE}` : '2px solid transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
        border: `2px solid ${Colors.PRIMARY_PURPLE}`,
        transform: 'scale(1.05)',
    },
}));

const ThumbnailImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
}));

const ImageCounter = styled(Typography)(() => ({
    color: Colors.NEUTRAL_WHITE,
    fontSize: '16px',
    fontWeight: 500,
}));

const LoadingContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '400px',
}));

const PhotoGalleryModal: React.FC<PhotoGalleryModalProps> = ({
    open,
    onClose,
    images,
    currentIndex,
    onIndexChange,
}) => {
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const thumbnailStripRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        setImageLoading(true);
        setImageError(false);
    }, [currentIndex]);

    // Auto-scroll to center active thumbnail
    useEffect(() => {
        if (open && thumbnailStripRef.current && thumbnailRefs.current[currentIndex]) {
            const scrollToCenter = () => {
                const container = thumbnailStripRef.current;
                const activeThumbnail = thumbnailRefs.current[currentIndex];
                
                if (container && activeThumbnail) {
                    const containerWidth = container.clientWidth;
                    const thumbnailLeft = activeThumbnail.offsetLeft;
                    const thumbnailWidth = activeThumbnail.offsetWidth;
                    
                    // Calculate the position to center the thumbnail
                    const targetScrollLeft = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);
                    
                    // Smooth scroll to center
                    container.scrollTo({
                        left: Math.max(0, targetScrollLeft),
                        behavior: 'smooth'
                    });
                }
            };
            
            // Small delay to ensure DOM is ready
            const timeoutId = setTimeout(scrollToCenter, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [currentIndex, open]);

    const handleThumbnailClick = (index: number) => {
        onIndexChange(index);
    };

    const handlePrevious = React.useCallback(() => {
        if (currentIndex > 0) {
            onIndexChange(currentIndex - 1);
        }
    }, [currentIndex, onIndexChange]);

    const handleNext = React.useCallback(() => {
        if (currentIndex < images.length - 1) {
            onIndexChange(currentIndex + 1);
        }
    }, [currentIndex, images.length, onIndexChange]);

    const handleKeyDown = React.useCallback((event: KeyboardEvent) => {
        if (!open) return;
        
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                handlePrevious();
                break;
            case 'ArrowRight':
                event.preventDefault();
                handleNext();
                break;
            case 'Escape':
                event.preventDefault();
                onClose();
                break;
        }
    }, [open, handlePrevious, handleNext, onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const handleImageLoad = () => {
        setImageLoading(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
        >
            <ModalHeader>
                <ImageCounter>
                    {currentIndex + 1} / {images.length}
                </ImageCounter>
                <CloseButton onClick={onClose}>
                    <i className="fi fi-br-cross" style={{ fontSize: '20px' }} />
                </CloseButton>
            </ModalHeader>

            <MainImageContainer>
                {imageLoading && (
                    <LoadingContainer>
                        <CircularProgress size={40} sx={{ color: Colors.NEUTRAL_WHITE }} />
                    </LoadingContainer>
                )}
                
                {imageError && (
                    <LoadingContainer>
                        <Typography color={Colors.NEUTRAL_WHITE}>
                            Failed to load image
                        </Typography>
                    </LoadingContainer>
                )}

                {images[currentIndex] && !imageError && (
                    <MainModalImage
                        src={images[currentIndex]}
                        alt={`Photo ${currentIndex + 1}`}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        style={{ display: imageLoading ? 'none' : 'block' }}
                    />
                )}

                {/* Navigation Buttons */}
                <PrevButton
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                >
                    <i className="fi fi-br-angle-left" style={{ fontSize: '24px' }} />
                </PrevButton>

                <NextButton
                    onClick={handleNext}
                    disabled={currentIndex === images.length - 1}
                >
                    <i className="fi fi-br-angle-right" style={{ fontSize: '24px' }} />
                </NextButton>
            </MainImageContainer>

            {/* Thumbnail Strip */}
            <ThumbnailStrip ref={thumbnailStripRef}>
                {images.map((image, index) => (
                    <ThumbnailWrapper
                        key={index}
                        ref={(el: HTMLDivElement | null) => {
                            thumbnailRefs.current[index] = el;
                        }}
                        isActive={index === currentIndex}
                        onClick={() => handleThumbnailClick(index)}
                    >
                        <ThumbnailImage
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                        />
                    </ThumbnailWrapper>
                ))}
            </ThumbnailStrip>
        </StyledDialog>
    );
};

export default PhotoGalleryModal;
